import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MdEdit, MdDelete } from 'react-icons/md';
import AdminService from "../../Services/AdminService";
import AdminData from "../../Models/AdminData";

import "./AdminUserView.css";
import AddUserModal from "../../Components/AddUserModal/AddUserModal";
import CreateUserDto from "../../Models/CreateUserDto";
import User from "../../Models/User";
import ConfirmDialog from "../../Components/ConfirmDialog/ConfirmDialog";
import Pagination from "../../Components/Pagination/Pagination";
import SearchBox from "../../Components/SearchBox/SearchBox";
import Loader from "../../Components/Loader/Loader";
import TokenExpiredPopUp from "../../Components/TokenExpiredPopUp/TokenExpiredPopUp";
import AddCompanyModal from "../../Components/AddCompanyModal/AddCompanyModal";
import { EntityTypes } from "../../Models/Store";
import CreateStoreDto from "../../Models/CreateStoreDto";

const AdminUserView: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const [tokenInvalide, setTokenInvalide] = useState<boolean>(false);

    const [adminData, setAdminData] = useState<AdminData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [showUserModal, setShowUserModal] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [updatingUser, setUpdatingUser] = useState(false);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [confirmDeleteUser, setConfirmDeleteUser] = useState<User | null>(null);
    const [editingStore, setEditingStore] = useState<any | null>(null);

    const [showUsersWithoutStore, setShowUsersWithoutStore] = useState(false);

    // pagination state
    const PAGE_SIZE = 7;
    const [usersPage, setUsersPage] = useState<number>(1);

    const [users, setUsers] = useState<User[]>([]);
    const [usersRaw, setUsersRaw] = useState<User[]>([]);
    const [usersQuery, setUsersQuery] = useState<string>("");
    const [usersTotal, setUsersTotal] = useState<number>(0);

    // load admin data
    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await AdminService.getAdminData(token);
            setAdminData(data);
        } catch (err: any) {
            if (err.status === 401) {
                setTokenInvalide(true);
            } else {
                setError("Une erreur inconnue est survenue. Veuillez réessayer plus tard ou contacter votre service informatique.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [token]);

    // fetch paged lists with fallback to adminData
    const fetchUsers = async (page: number) => {
        try {
            if (adminData) {
                const normalizedBase = (adminData.users || []).map(u => ({ ...u, companies: u.storeAccesses || [] }));
                const filtered = applyFilter(normalizedBase, usersQuery);
                const start = (page - 1) * PAGE_SIZE;
                const slice = filtered.slice(start, start + PAGE_SIZE);
                setUsersRaw(normalizedBase);
                setUsers(slice);
                setUsersTotal(filtered.length);
                return;
            }

            const res = await AdminService.getUsersPaged(token, page, PAGE_SIZE);
            const normalized = (res.items || []).map(u => ({ ...u, companies: u.storeAccesses || [] }));
            setUsersRaw(normalized);
            setUsers(applyFilter(normalized, usersQuery));
            setUsersTotal(res.totalItems);
        } catch (e) {
            setUsers([]);
            setUsersRaw([]);
            setUsersTotal(0);
        }
    };

    useEffect(() => { if (token) fetchUsers(usersPage); }, [token, usersPage, usersQuery, adminData, showUsersWithoutStore]);
    // suppression du chargement de la table entités côté front

    // reset pages when data or filters change
    useEffect(() => {
        setUsersPage(1);
    }, [adminData, usersQuery, showUsersWithoutStore]);

    const applyFilter = (items: User[], query: string) => {
        const q = (query || "").toLowerCase();
        let base = items;
        if (showUsersWithoutStore) {
            base = base.filter(u => (u.storeAccesses || []).length === 0);
        }
        if (!q) return base;
        return base.filter(u => u.email.toLowerCase().includes(q) || (u.storeAccesses || []).some(s => s.name.toLowerCase().includes(q)));
    };

    
 
    
    if (loading) return <Loader label = "Chargement..."/>;

    return (
        <div id="AdminUser-View" className="admin-dashboard">
            { tokenInvalide && <TokenExpiredPopUp/>}
            {error ? (
                <p className="Error">{error}</p>
            ) : (
                <div className="dashboard-content">
                    {/* USERS */}
                    <div className="user-management">
                        <h2>Gérer les Utilisateurs</h2>
                        <div className="section-header">
                            <button className="btn" onClick={() => setShowUserModal(true)}>
                                + Ajouter un Utilisateur
                            </button>
                            <button
                                className={`btn btn-toggle${showUsersWithoutStore ? ' active' : ''}`}
                                onClick={() => setShowUsersWithoutStore(prev => !prev)}
                                title="Afficher uniquement les utilisateurs sans magasin"
                            >
                                {showUsersWithoutStore ? 'Tous les utilisateurs' : 'Sans magasin'}
                            </button>
                            <SearchBox onSearch={setUsersQuery} className="section-search" searchQuery={usersQuery} />
                        </div>

                        {createError && <p className="Error">{createError}</p>}
                        {updateError && <p className="Error">{updateError}</p>}
                        {deleteError && <p className="Error">{deleteError}</p>}

                        <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Rôle</th>
                                    <th>Magasins</th>
                                    <th className="table-actions"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user) => (
                                    <tr key={user.id}>
                                        <td data-label="Email">{user.email}</td>
                                        <td data-label="Rôle">{user.role}</td>
                                        <td data-label="Magasins">
                                            <div className="tables-company-items">
                                                {(user.storeAccesses || []).map((store: any) => (
                                                    <div 
                                                        className="Company" 
                                                        key={store.id}
                                                        onClick={() => setEditingStore(store)}
                                                    >
                                                        {store.name}
                                                    </div>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="table-actions" data-label="">
                                            <button
                                                className="btn-edit"
                                                onClick={() => { setUpdateError(null); setEditingUser(user); }}
                                            >
                                                <MdEdit className="btn-edit-icone" />
                                            </button>
                                            <button
                                                className="btn-icon btn-danger"
                                                title="Supprimer"
                                                disabled={deletingUserId === user.id}
                                                onClick={() => { setDeleteError(null); setConfirmDeleteUser(user); }}
                                            >
                                                <MdDelete className="btn-delete-icone" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                        {(
                            <Pagination
                                currentPage={usersPage}
                                totalItems={usersTotal}
                                pageSize={PAGE_SIZE}
                                onChange={setUsersPage}
                            />
                        )}
                    </div>

                    {/* USER CREATE */}
                    {showUserModal && adminData && (
                        <AddUserModal
                            companies={adminData.stores}
                            onClose={() => { setCreateError(null); setShowUserModal(false); }}
                            onSave={async (data: CreateUserDto) => {
                                setCreateError(null);
                                try {
                                    await AdminService.createUser(token, data);
                                    await fetchData();
                                    setShowUserModal(false);
                                } catch (err: any) {
                                    setCreateError("Impossible de créer l'utilisateur. Veuillez réessayer.");
                                    throw err;
                                }
                            }}
                        />
                    )}

                    {/* USER EDIT */}
                    {editingUser && adminData && (
                        <AddUserModal
                            mode="edit"
                            titleOverride="Modifier un Utilisateur"
                            submitLabelOverride={updatingUser ? "Mise à jour..." : "Mettre à jour"}
                            companies={adminData.stores}
                            initialValues={{
                                email: editingUser.email,
                                role: editingUser.role as "Admin" | "User",
                                storeIds: (editingUser.storeAccesses || []).map((c: any) => c.id),
                            }}
                            onClose={() => { setUpdateError(null); setEditingUser(null); }}
                            onSave={async (data) => {
                                setUpdateError(null);
                                setUpdatingUser(true);
                                try {
                                    await AdminService.updateUser(token, editingUser.id, {
                                        email: data.email,
                                        role: data.role,
                                        storeIds: data.storeIds,
                                    });
                                    await fetchData();
                                    setEditingUser(null);
                                } catch (err: any) {
                                    setUpdateError("Impossible de mettre à jour l'utilisateur. Veuillez réessayer.");
                                    throw err;
                                } finally {
                                    setUpdatingUser(false);
                                }
                            }}
                        />
                    )}

                    {/* USER DELETE */}
                    <ConfirmDialog
                        open={!!confirmDeleteUser}
                        title="Supprimer l'utilisateur"
                        message={
                            confirmDeleteUser ? (
                                <>Voulez-vous vraiment supprimer <b>{confirmDeleteUser.email}</b> ? Cette action est irréversible.</>
                            ) : null
                        }
                        confirmText="Supprimer"
                        cancelText="Annuler"
                        danger
                        loading={!!deletingUserId}
                        onCancel={() => setConfirmDeleteUser(null)}
                        onConfirm={async () => {
                            if (!confirmDeleteUser) return;
                            try {
                                setDeletingUserId(confirmDeleteUser.id);
                                await AdminService.deleteUser(token, confirmDeleteUser.id);
                                await fetchData();
                                setConfirmDeleteUser(null);
                            } catch (e: any) {
                                setDeleteError("Impossible de supprimer l'utilisateur. Réessayez.");
                            } finally {
                                setDeletingUserId(null);
                            }
                        }}
                    />

                    {/* STORE EDIT */}
                    {editingStore && adminData && (
                        <AddCompanyModal
                            mode="edit"
                            initialValues={{
                                name: editingStore.name,
                                codes: editingStore.codes,
                                selectedEntityTypes: editingStore.entities,
                            }}
                            onClose={() => setEditingStore(null)}
                            submitLabelOverride={updatingUser ? "Mise à jour..." : "Mettre à jour"}
                            onSave={async (data: { name: string; codes: string[]; selectedEntityTypes: EntityTypes[] }) => {
                                setUpdatingUser(true);
                                try {
                                    await AdminService.updateStore(token, editingStore.id, { name: data.name, codes: data.codes, entities: data.selectedEntityTypes } as CreateStoreDto);
                                    await fetchData();
                                    setEditingStore(null);
                                } catch (err: any) {
                                    let msg = "Impossible de mettre à jour le magasin. Veuillez réessayer.";
                                    if (err?.status === 400) {
                                        try {
                                            const json = JSON.parse(err.message);
                                            if (json?.message) msg = json.message;
                                        } catch {}
                                    } else if (err?.status === 409) {
                                        try {
                                            const json = JSON.parse(err.message);
                                            if (json?.message) msg = json.message;
                                        } catch {}
                                    } else if (err?.status === 404) {
                                        msg = "Le magasin demandé est introuvable.";
                                    }
                                    throw err;
                                } finally {
                                    setUpdatingUser(false);
                                }
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminUserView;
